import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

export type SubscriptionStatusModalKind = 'grace' | 'expired';

@Component({
  selector: 'app-subscription-status-modal',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm"
         role="dialog" aria-modal="true" aria-labelledby="subStatusTitle">
      <div class="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7 relative">

        <div class="flex justify-center mb-4">
          <div class="w-14 h-14 rounded-full flex items-center justify-center ring-8"
               [class.bg-amber-100]="kind === 'grace'"
               [class.ring-amber-50]="kind === 'grace'"
               [class.bg-red-100]="kind === 'expired'"
               [class.ring-red-50]="kind === 'expired'">
            <svg class="w-7 h-7"
                 [class.text-amber-600]="kind === 'grace'"
                 [class.text-red-600]="kind === 'expired'"
                 fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            </svg>
          </div>
        </div>

        <h2 id="subStatusTitle" class="text-xl font-bold text-center text-gray-900 mb-1">
          {{ kind === 'grace' ? 'Renewal is due soon' : 'Subscription expired' }}
        </h2>
        <p class="text-sm text-center text-gray-500 mb-5 leading-relaxed">
          <ng-container *ngIf="kind === 'grace'">
            Your <strong>{{ planName || 'subscription' }}</strong> is in its grace period.
            Renew before <strong>{{ formatDate(graceEndsAt) }}</strong> to keep all features.
          </ng-container>
          <ng-container *ngIf="kind === 'expired'">
            Your <strong>{{ planName || 'subscription' }}</strong> ended on
            <strong>{{ formatDate(currentPeriodEnd) }}</strong>. Submit a payment or
            request an exemption to restore access.
          </ng-container>
        </p>

        <ng-container *ngIf="kind === 'grace'">
          <div class="flex flex-col gap-2">
            <a routerLink="/billing/submit-payment" (click)="dismiss.emit()"
               class="w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors">
              Submit payment
            </a>
            <button type="button" (click)="dismiss.emit()"
                    class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 rounded-lg transition-colors">
              Continue
            </button>
          </div>
        </ng-container>

        <ng-container *ngIf="kind === 'expired'">
          <div class="flex flex-col gap-2">
            <a routerLink="/billing" (click)="continueReadOnly.emit()"
               class="w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors">
              Continue (read-only)
            </a>
            <a routerLink="/billing/request-exemption" (click)="continueReadOnly.emit()"
               class="w-full text-center bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 font-medium py-2.5 rounded-lg transition-colors">
              Request exemption
            </a>
            <button type="button" (click)="logout.emit()"
                    class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 rounded-lg transition-colors">
              OK — log out
            </button>
          </div>
        </ng-container>

      </div>
    </div>
  `
})
export class SubscriptionStatusModalComponent {
  @Input({ required: true }) kind: SubscriptionStatusModalKind = 'grace';
  @Input() planName: string | null = null;
  @Input() currentPeriodEnd: string | null = null;
  @Input() graceEndsAt: string | null = null;

  @Output() dismiss = new EventEmitter<void>();
  @Output() continueReadOnly = new EventEmitter<void>();
  @Output() logout = new EventEmitter<void>();

  formatDate(d: string | null): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString();
  }
}
